import { useCallback, useEffect, useState } from "react";
import {
  Plus, Pencil, Trash2, Eye, Loader2, Save, Heart, Upload, Undo2, Newspaper,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { AdminGuard } from "../components/admin/AdminGuard";
import { AdminNav } from "../components/admin/AdminNav";
import { Reveal } from "../components/Reveal";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";

const inputClass =
  "h-11 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25";
const textareaClass =
  "w-full rounded-xl border border-input bg-white p-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25";

const wordCount = (t) => ((t || "").trim().match(/\S+/g) || []).length;

const EMPTY_DRAFT = {
  title: "", slug: "", excerpt: "", content: "", category: "Admissions",
  tags: "", image_url: "", author: "LearnYourWay Team", status: "draft",
};

function BlogPreview({ blog }) {
  return (
    <div className="px-7 py-7" data-testid="blog-preview">
      {blog.image_url && (
        <img src={blog.image_url} alt="" className="mb-6 aspect-[16/8] w-full rounded-2xl object-cover" />
      )}
      <span className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-dark">
        {blog.category}
      </span>
      <h2 className="mt-4 font-display text-2xl font-extrabold leading-tight text-forest-deep">{blog.title}</h2>
      <p className="mt-2 text-xs text-muted-foreground">
        {blog.author} · {wordCount(blog.content)} words · {Math.max(1, Math.round(wordCount(blog.content) / 200))} min read
      </p>
      <div className="mt-6">
        {(blog.content || "").split("\n\n").filter(Boolean).map((p, i) => (
          <p key={i} className="mb-5 text-[15px] leading-[1.8] text-foreground/85">{p}</p>
        ))}
      </div>
    </div>
  );
}

export default function AdminBlogsPage() {
  const [blogs, setBlogs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/blogs");
      setBlogs(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setBlogs([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => setEditing({ isNew: true, draft: { ...EMPTY_DRAFT } });
  const openEdit = (blog) =>
    setEditing({ isNew: false, id: blog.id, draft: { ...blog, tags: (blog.tags || []).join("\n") } });

  const save = async () => {
    const d = editing.draft;
    if (d.title.trim().length < 3) return toast.error("Title is required (min 3 characters)");
    setSaving(true);
    try {
      const payload = {
        ...d,
        tags: String(d.tags || "").split("\n").map((t) => t.trim()).filter(Boolean),
        slug: d.slug?.trim() || undefined,
      };
      if (editing.isNew) await api.post("/admin/blogs", payload);
      else await api.put(`/admin/blogs/${editing.id}`, payload);
      toast.success(editing.isNew ? "Post created" : "Post updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (blog) => {
    try {
      const { data } = await api.post(`/admin/blogs/${blog.id}/publish`);
      toast.success(data.status === "published" ? `"${blog.title}" is live` : `"${blog.title}" moved to drafts`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (blog) => {
    if (confirmDelete !== blog.id) {
      setConfirmDelete(blog.id);
      setTimeout(() => setConfirmDelete((c) => (c === blog.id ? null : c)), 3000);
      return;
    }
    try {
      await api.delete(`/admin/blogs/${blog.id}`);
      toast.success("Post deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const words = editing ? wordCount(editing.draft.content) : 0;
  const wordsOk = words >= 150 && words <= 500;

  return (
    <AdminGuard>
      <div className="min-h-screen bg-bone pt-[72px]" data-testid="admin-blogs-page">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">Admin — Publishing</p>
            <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-forest-deep sm:text-4xl">
              Blog Manager
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Write, preview, and publish articles. Posts go live at 150–500 words.
            </p>
            <AdminNav />
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-8 flex items-center justify-between">
              <p className="text-sm text-muted-foreground" data-testid="blogs-count">
                {blogs ? `${blogs.length} post${blogs.length === 1 ? "" : "s"} · ${blogs.filter((b) => b.status === "published").length} live` : ""}
              </p>
              <button onClick={openNew} data-testid="blog-add-btn"
                className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-display text-sm font-bold text-forest-deep transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(212,175,55,0.4)]">
                <Plus className="h-4 w-4" /> New post
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.14}>
            <div className="mt-6 space-y-3" data-testid="admin-blogs-list">
              {blogs === null ? (
                <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>
              ) : blogs.length === 0 ? (
                <p className="rounded-3xl border border-dashed border-border bg-white py-14 text-center text-sm text-muted-foreground">
                  No posts yet — write your first one.
                </p>
              ) : (
                blogs.map((blog) => (
                  <div key={blog.id} data-testid={`admin-blog-row-${blog.slug}`}
                    className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-white px-5 py-4 transition-shadow duration-200 hover:shadow-[0_10px_30px_rgba(10,41,30,0.07)] sm:flex-nowrap sm:gap-4">
                    <span className={`shrink-0 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${
                      blog.status === "published"
                        ? "border-gold/40 bg-gold/15 text-gold-dark"
                        : "border-stone-200 bg-stone-100 text-stone-500"
                    }`}>
                      {blog.status}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-forest-deep">{blog.title}</p>
                      <p className="mt-0.5 flex flex-wrap items-center gap-2.5 text-xs text-muted-foreground">
                        <span>{blog.category}</span>
                        <span>·</span>
                        <span className={blog.status !== "published" && !(blog.word_count >= 150 && blog.word_count <= 500) ? "font-semibold text-amber-600" : ""}>
                          {blog.word_count ?? wordCount(blog.content)} words
                        </span>
                        <span className="flex items-center gap-1"><Heart className="h-3 w-3 text-gold-dark" />{blog.likes || 0}</span>
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button onClick={() => setPreview(blog)} data-testid={`blog-preview-${blog.slug}`} title="Preview"
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-forest transition-colors hover:border-forest/40">
                        <Eye className="h-4 w-4" />
                      </button>
                      <button onClick={() => openEdit(blog)} data-testid={`blog-edit-${blog.slug}`} title="Edit"
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-forest transition-colors hover:border-forest/40">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => togglePublish(blog)} data-testid={`blog-publish-${blog.slug}`}
                        title={blog.status === "published" ? "Unpublish" : "Publish"}
                        className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
                          blog.status === "published"
                            ? "border-stone-200 text-stone-500 hover:border-stone-400"
                            : "border-forest/30 text-forest hover:border-forest"
                        }`}>
                        {blog.status === "published" ? <Undo2 className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
                      </button>
                      <button onClick={() => remove(blog)} data-testid={`blog-delete-${blog.slug}`} title="Delete"
                        className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
                          confirmDelete === blog.id
                            ? "border-destructive bg-destructive text-white"
                            : "border-border text-destructive/60 hover:border-destructive/50"
                        }`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Reveal>
        </div>

        <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <SheetContent side="right" className="w-full overflow-y-auto bg-bone p-0 sm:max-w-xl" data-testid="blog-editor-sheet">
            {editing && (
              <div>
                <div className="bg-forest-deep px-7 py-6">
                  <SheetTitle className="font-display text-lg font-bold text-bone">
                    {editing.isNew ? "New post" : "Edit post"}
                  </SheetTitle>
                  <p className="mt-1 text-xs text-emerald-100/60">
                    Save as draft freely — publishing requires 150–500 words.
                  </p>
                </div>
                <div className="space-y-5 px-7 py-7">
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Title *</label>
                    <input data-testid="blog-field-title" className={inputClass} value={editing.draft.title}
                      onChange={(e) => setEditing((x) => ({ ...x, draft: { ...x.draft, title: e.target.value } }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Category</label>
                      <input data-testid="blog-field-category" className={inputClass} value={editing.draft.category}
                        list="blog-categories"
                        onChange={(e) => setEditing((x) => ({ ...x, draft: { ...x.draft, category: e.target.value } }))} />
                      <datalist id="blog-categories">
                        {["Admissions", "Careers", "Language Tests", "Quranic Studies", "Study Abroad", "General"].map((c) => (
                          <option key={c} value={c} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Author</label>
                      <input data-testid="blog-field-author" className={inputClass} value={editing.draft.author}
                        onChange={(e) => setEditing((x) => ({ ...x, draft: { ...x.draft, author: e.target.value } }))} />
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                      Slug <span className="normal-case tracking-normal">(leave empty to auto-generate)</span>
                    </label>
                    <input data-testid="blog-field-slug" className={inputClass} value={editing.draft.slug || ""}
                      placeholder="my-article-url" onChange={(e) => setEditing((x) => ({ ...x, draft: { ...x.draft, slug: e.target.value } }))} />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Excerpt</label>
                    <textarea data-testid="blog-field-excerpt" rows={2} className={textareaClass} value={editing.draft.excerpt}
                      placeholder="One or two sentences shown on cards"
                      onChange={(e) => setEditing((x) => ({ ...x, draft: { ...x.draft, excerpt: e.target.value } }))} />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Cover image URL</label>
                    <input data-testid="blog-field-image" className={inputClass} value={editing.draft.image_url || ""}
                      placeholder="https://…" onChange={(e) => setEditing((x) => ({ ...x, draft: { ...x.draft, image_url: e.target.value } }))} />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Tags (one per line)</label>
                    <textarea data-testid="blog-field-tags" rows={2} className={textareaClass} value={editing.draft.tags}
                      onChange={(e) => setEditing((x) => ({ ...x, draft: { ...x.draft, tags: e.target.value } }))} />
                  </div>
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Content <span className="normal-case tracking-normal">(blank line between paragraphs)</span>
                      </label>
                      <span data-testid="blog-word-count"
                        className={`font-mono text-[10px] font-semibold ${wordsOk ? "text-emerald-600" : "text-amber-600"}`}>
                        {words} / 150–500 words
                      </span>
                    </div>
                    <textarea data-testid="blog-field-content" rows={12} className={textareaClass} value={editing.draft.content}
                      onChange={(e) => setEditing((x) => ({ ...x, draft: { ...x.draft, content: e.target.value } }))} />
                  </div>
                  <div className="flex gap-3">
                    <button onClick={save} data-testid="blog-save-btn" disabled={saving}
                      className="flex flex-1 items-center justify-center gap-2 rounded-full bg-forest py-3.5 font-display text-sm font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      {editing.isNew ? "Save post" : "Save changes"}
                    </button>
                    <button onClick={() => setPreview(editing.draft)} data-testid="blog-preview-btn"
                      className="flex items-center gap-2 rounded-full border border-border px-6 text-sm font-semibold text-foreground/70 transition-colors hover:text-forest">
                      <Eye className="h-4 w-4" /> Preview
                    </button>
                  </div>
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>

        <Sheet open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
          <SheetContent side="right" className="w-full overflow-y-auto bg-bone p-0 sm:max-w-xl" data-testid="blog-preview-sheet">
            {preview && (
              <div>
                <div className="bg-forest-deep px-7 py-5">
                  <SheetTitle className="flex items-center gap-2 font-display text-base font-bold text-bone">
                    <Newspaper className="h-4 w-4 text-gold" /> Preview — as readers will see it
                  </SheetTitle>
                </div>
                <BlogPreview blog={preview} />
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AdminGuard>
  );
}
